import { TableItem } from 'components/atoms';
import { LabeledStatus } from 'components/molecules';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useInfiniteTable } from 'hooks/useInfiniteTable';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { useHasPermission } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { useGetContractsToExpire } from 'services/contract';
import { formatDate } from 'utils/global/date.utils';
import { usePermissionGuard } from "hooks/usePermissionGuard";

enum COLUMNS {
  title,
  label,
  startDate,
  endDate,
  status,
}

const ContractsToExpire = () => {
  usePermissionGuard('ContractPermissionsLIST**');
  const hasPermission = useHasPermission();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const columnsVisibility = useTableColumnsVisibility(
    'ContractsToExpire',
    COLUMNS
  );

  const contractsToExpire = useGetContractsToExpire();

  const tableProps = useInfiniteTable(contractsToExpire, { selectable: false });

  const tableData = {
    nodes: tableProps.data.nodes.map(data => ({
      ...data,
      id: `/label/${data.labelId}/contract/${data.id}/form`,
    })),
  };

  const hasDetailPermission = hasPermission('ContractPermissionsLIST**', 128);

  const onSelectChange = (action: Action, state: State) => {
    if (!hasDetailPermission) {
      return;
    }
    
    const id: string = state.id ?? state.ids[state.ids.length - 1];
    navigate(id, { state: 'contracts-to-expire' });
  };

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });

  const tableSelect = hasDetailPermission ? select : undefined;

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`navbar.contracts-to-expire`)}
          path=''
          hideBackButton
          topbarContent={
            <div className='flex'>
              <Table.CustomColumnsButton
                columns={COLUMNS}
                isClear
                visibility={columnsVisibility}
                customI18next=''
                i18nextPrefix={'input.label'}
              />
            </div>
          }
        />
      }>
      <Table
        id={'ContractsToExpire'}
        loading={tableProps.loading}
        onFullyScroll={tableProps.onFullyScroll}
        select={tableSelect}
        data={tableData}
        displayMode
        columns={[
          {
            label: t('input.label.title'),
            renderCell: ({ title }) => <TableItem>{title}</TableItem>,
            resize: true,
            hide: columnsVisibility.hide(COLUMNS.title),
          },
          {
            label: t('input.label.label'),
            renderCell: ({ labelName }) => <TableItem>{labelName}</TableItem>,
            resize: true,
            hide: columnsVisibility.hide(COLUMNS.label),
          },
          {
            label: t('input.label.startDate'),
            renderCell: ({ startDate }) => (
              <TableItem>{formatDate(startDate)}</TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(COLUMNS.startDate),
          },
          {
            label: t('input.label.endDate'),
            renderCell: ({ endDate }) => (
              <TableItem>{endDate ? formatDate(endDate) : '-'}</TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(COLUMNS.endDate),
          },
          {
            label: t('input.label.status'),
            renderCell: ({ status }) => (
              <TableItem justifyPosition='center'>
                <LabeledStatus.Contract status={status} />
              </TableItem>
            ),
            hide: columnsVisibility.hide(COLUMNS.status),
          },
        ]}
      />
    </TopbarPage>
  );
};

export default ContractsToExpire;
